import {
  Grid,
  Radio,
  RadioGroup,
  Select,
  Space,
  TextInput,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { useEffect, useState } from 'react';
import { CurrenciesData } from '../../../../data/currencies-data.array';
import {
  BankList,
  BusinessBankAccountDepositTypes,
  BusinessBankAccountTypes,
} from '../../../../enums/bank.enums';
import { GetBankNamesData } from '../../../../helpers/enum.helper';
import { MerchantOnboardingFormValues } from '../../../../type/merchant-onboarding-form-values.types';

interface Props {
  index: number;
  form: UseFormReturnType<MerchantOnboardingFormValues>;
}

function Settlement(props: Props) {
  const { index, form } = props;

  const [bankType, setBankType] = useState(
    form.values.settlements[index].accountDepositTypeId
  );

  const bankListData = GetBankNamesData();

  useEffect(() => {
    if (bankType === BusinessBankAccountDepositTypes.NO_ACCOUNT) {
      form.setListItem('settlements', index, {
        ...form.values.settlements[index],
        bankName: 'n/a',
        accountNumber: 'n/a',
        accountName: 'n/a',
      });
    } else if (bankType === BusinessBankAccountDepositTypes.PAYMAYA) {
      form.setListItem('settlements', index, {
        ...form.values.settlements[index],
        bankName: BankList.PAYMAYA,
        accountNumber: '',
        accountName: '',
      });
    } else if (bankType === BusinessBankAccountDepositTypes.GCASH) {
      form.setListItem('settlements', index, {
        ...form.values.settlements[index],
        bankName: BankList.GCASH,
        accountNumber: '',
        accountName: '',
      });
    } else {
      form.setListItem('settlements', index, {
        ...form.values.settlements[index],
        bankName: '',
        accountNumber: '',
        accountName: '',
      });
    }
  }, [bankType]);

  const label = {
    accountDepositTypeId: 'Deposit to Account',
    bankName: 'Bank name',
    accountName: 'Account name',
    accountNumber: 'Account number',
    accountTypeId: 'Account type',
    currency: 'Currency',

    isWantPackworksWallet: 'Would you like to open a Packworks wallet?',
    isWantPayMayaBankWallet: 'Would you like to open a Maya Bank Account?',
    isWantMayaWallet: 'Would you like to open a Maya wallet?',
  };
  const placeHolder = {
    accountDepositTypeId: 'Select deposit account',
    bankName: 'Select bank name',
    accountName: 'Account name',
    accountNumber: 'Account number',
    currency: 'Currency',
  };

  return (
    <>
      <Grid.Col xs={12} sm={6}>
        <Select
          label={placeHolder.accountDepositTypeId}
          placeholder={placeHolder.accountDepositTypeId}
          value={form.values.settlements[index].accountDepositTypeId.toString()}
          onChange={(value: string) => {
            form.setListItem('settlements', index, {
              ...form.values.settlements[index],
              accountDepositTypeId: parseInt(value),
            });
            setBankType(parseInt(value));
          }}
          data={[
            {
              value: BusinessBankAccountDepositTypes.PAYMAYA.toString(),
              label: 'PAYMAYA',
            },
            {
              value: BusinessBankAccountDepositTypes.GCASH.toString(),
              label: 'GCASH',
            },
            {
              value: BusinessBankAccountDepositTypes.BANK.toString(),
              label: 'BANK',
            },
            {
              value: BusinessBankAccountDepositTypes.NO_ACCOUNT.toString(),
              label: 'NO ACCOUNT',
            },
          ]}
          error={
            form.getListInputProps('settlements', index, 'accountDepositTypeId')
              .error
          }
          data-cy='accountType'
        />
      </Grid.Col>
      {bankType === BusinessBankAccountDepositTypes.BANK && (
        <Grid.Col xs={12} sm={6}>
          <Select
            label={placeHolder.bankName}
            placeholder={placeHolder.bankName}
            value={form.values.settlements[index].bankName}
            onChange={(value: string) => {
              form.setListItem('settlements', index, {
                ...form.values.settlements[index],
                bankName: value,
              });
            }}
            data={bankListData}
            searchable
            nothingFound='No options'
            error={
              form.getListInputProps('settlements', index, 'bankName').error
            }
            data-cy='bankName'
          />
        </Grid.Col>
      )}
      {bankType !== BusinessBankAccountDepositTypes.NO_ACCOUNT && (
        <>
          <Grid.Col>
            <TextInput
              placeholder={placeHolder.accountName}
              label={label.accountName}
              value={form.values.settlements[index].accountName}
              onChange={(event) =>
                form.setListItem('settlements', index, {
                  ...form.values.settlements[index],
                  accountName: event.currentTarget.value,
                })
              }
              error={
                form.getListInputProps('settlements', index, 'accountName')
                  .error
              }
              data-cy='accountName'
            />
          </Grid.Col>
          <Grid.Col>
            <TextInput
              placeholder={placeHolder.accountNumber}
              label={label.accountNumber}
              value={form.values.settlements[index].accountNumber}
              onChange={(event) =>
                form.setListItem('settlements', index, {
                  ...form.values.settlements[index],
                  accountNumber: event.currentTarget.value,
                })
              }
              error={
                form.getListInputProps('settlements', index, 'accountNumber')
                  .error
              }
              data-cy='accountNumber'
            />
          </Grid.Col>
          <Grid.Col>
            <Select
              placeholder={placeHolder.currency}
              label={label.currency}
              value={
                form.values.settlements[index].currencyId &&
                form.values.settlements[index].currencyId?.toString()
              }
              data={CurrenciesData}
              onChange={(value: string) => {
                form.setListItem('settlements', index, {
                  ...form.values.settlements[index],
                  currencyId: parseInt(value),
                });
              }}
              error={
                form.getListInputProps('settlements', index, 'currencyId').error
              }
            />
          </Grid.Col>
        </>
      )}
      {bankType === BusinessBankAccountDepositTypes.BANK && (
        <Grid.Col>
          <RadioGroup
            label={label.accountTypeId}
            sx={{ '& label': { fontSize: 14 } }}
            onChange={(value: string) => {
              form.setListItem('settlements', index, {
                ...form.values.settlements[index],
                accountTypeId: parseInt(value),
              });
            }}
            value={form.values.settlements[index].accountTypeId?.toString()}
            data-cy='accountTypeId'
          >
            <Radio
              value={BusinessBankAccountTypes.SAVINGS.toString()}
              label='Savings'
            />
            <Radio
              value={BusinessBankAccountTypes.CHECKING_CURRENT.toString()}
              label='Checking/Current'
            />
          </RadioGroup>
          <Space h='sm' />
        </Grid.Col>
      )}
    </>
  );
}

export default Settlement;
